import React, { Dispatch, useEffect, useState } from 'react';
import { FormBuilder } from '@eplass/formdesigner';
import { Box, CircularProgress } from '@mui/material';
/** Must be imported from one origin */
import { UiRootElement, dynamicFormName } from '@eplass/formdesigner/domain';
import { IProjectDocType } from '../../../redux/project/sliceProjectDocTypes';
import { useParams } from 'react-router-dom';
import {
    IDocumentDetailsEntry,
    getFormData,
    getPermissions,
    getProjectDocumentDetails,
    handleEditMode,
    saveForm,
    saveSchema,
    toggleIsDocumentCreated,
    toggleSaveButton,
    setSaveButtonLoading,
} from '../../../redux/project/documents/sliceProjectDocumentDetails';
import { createDocument } from '../../../redux/project/documents/sliceProjectUploadAdditionalDocuments';
import { useTranslation } from 'react-i18next';
import { headingFontBold } from '../../../Theme';
import { useDispatch, useSelector } from 'react-redux';
import store, { IAppReduxStore } from '../../../redux/store';
import DocumentDetailsError from '../../Views/DocumentDetailsView/DocumentDetailsError';
import { isEqual, has } from 'lodash';
import { FormdesignersModeTypes } from '@eplass/formdesigner/domain/mode.types';
import {
    getActionsForFormdesigner,
    getProjectActionsConfig,
} from '../../../redux/project/sliceProjectActions';
import { fetchDocuments, fetchViewData } from '../../../util/ProjectUtils';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../Toast/Toast';
import { langs } from '../../../localization/i18n';

/** Component that provides FormDesigner for DocumentDetails Dialog. */
export default ({
    submitRef,
    docType,
    documentDetails,
    defaultFormData = {},
}: IFormDesignerProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'formdesigner' });

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** State slice that controls the formbuilder's mode. */
    const [edit, setEdit] = useState(false);

    /** Get the replicaId from the react router. */
    const params = useParams();
    const { replicaId, areaName } = params;

    /** Access relevant state variables from the redux store. */
    const {
        formdesignerData: { data: formData, loading: formDataLoading, error: formDataError },
        formdesignerPermissions: {
            data: permissions,
            loading: permissionsLoading,
            error: permissionsError,
        },
        documentDetailsMode: formdesignersMode,
        docTypeKeyForTemplateCreation,
        isDialogExpanded,
    } = useSelector((store: IAppReduxStore) => store.projectDocumentDetails);

    const availableActionsList = useSelector((state: IAppReduxStore) =>
        getActionsForFormdesigner(state, {
            replicaId,
            universalId: documentDetails.UniversalID,
            jsonSchema: JSON.stringify(docType.detailSchema),
        }),
    );

    useEffect(() => {
        dispatch(getProjectActionsConfig(replicaId));
    }, []);

    useEffect(() => {
        if (!docType.detailSchema) return;
        const schemaData = JSON.stringify(docType.detailSchema);
        const { UniversalID } = documentDetails;
        dispatch(
            getPermissions({
                replicaId,
                universalId: UniversalID,
                schemaData,
                docTypeKey: docType.name,
            }),
        );
        if (!docTypeKeyForTemplateCreation) {
            dispatch(
                getFormData({
                    replicaId,
                    universalId: UniversalID,
                    schemaData,
                }),
            );
        }
    }, [docType.detailSchema, documentDetails, docTypeKeyForTemplateCreation]);

    const onSave = async data => {
        try {
            store.dispatch(setSaveButtonLoading(true));
            const newObj = Object.keys(data).reduce((obj, item) => {
                if (
                    (data[item] === '' && has(formData, item)) ||
                    (!!data[item] && !isEqual(data[item], formData?.[item]))
                ) {
                    obj[item] = data[item];
                }
                return obj;
            }, {});

            let universalId = documentDetails?.UniversalID;

            if (docTypeKeyForTemplateCreation) {
                universalId = (
                    (
                        await store.dispatch(
                            createDocument({
                                docType: docTypeKeyForTemplateCreation,
                                replicaId,
                            }),
                        )
                    ).payload as { docUnId: string }
                ).docUnId;
            }

            const promises = [
                () =>
                    store.dispatch(
                        saveForm({
                            replicaId,
                            universalId,
                            updatedForm: JSON.stringify(newObj),
                        }),
                    ),
                () =>
                    docTypeKeyForTemplateCreation
                        ? Promise.resolve(store.dispatch(toggleIsDocumentCreated(true)))
                        : store.dispatch(
                              getProjectDocumentDetails({
                                  replicaId,
                                  universalId,
                              }),
                          ),
            ];

            let hasError = false;
            for (const element of promises) {
                const result = await element();
                if (result?.type?.includes('rejected')) {
                    hasError = true;
                    break;
                }
            }

            store.dispatch(setSaveButtonLoading(false));

            if (hasError) {
                return;
            }
            dispatch(handleEditMode(FormdesignersModeTypes.View));
            areaName === 'filing' ? fetchViewData(params) : fetchDocuments(params);
        } catch (e: unknown) {
            store.dispatch(setSaveButtonLoading(false));
            enqueueSnackbar((e as Error)?.message || t('errorDocumentCreation'), {
                severity: ToastSeverity.Error,
            });
        }
    };

    const onSaveSChema = data => {
        dispatch(saveSchema(data));
    };

    /**
     * Changes the edit mode.
     * @param value true or false.
     */
    const onToggleEdit = (value: boolean) => {
        setEdit(value);
    };

    if ((!docTypeKeyForTemplateCreation && formDataLoading) || permissionsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ display: 'flex', justifyContent: 'center' }} />
            </Box>
        );
    }

    if (Object.keys(docType.detailSchema).length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4, ...headingFontBold }}>
                {t('emptyDocumentDetailsSchema')}
            </Box>
        );
    }

    if (formDataError || permissionsError) {
        return <DocumentDetailsError />;
    }

    return (
        <FormBuilder
            initialUiSchema={docType.detailSchema as unknown as UiRootElement}
            formData={{
                values: {
                    [dynamicFormName]: [formData ?? defaultFormData],
                },
            }}
            handlers={{
                save: onSave,
                edit: onToggleEdit,
                saveSchema: onSaveSChema,
            }}
            actionList={availableActionsList}
            edit={edit}
            formdesignersMode={formdesignersMode}
            permissions={permissions}
            formRef={submitRef}
            defineIsFormDataChanged={data => dispatch(toggleSaveButton(data))}
            handleSubmitSuccess={onSave}
            handleSubmitError={data => console.log(data)}
            mandatoryLangs={['en', 'de']}
            availableLangsList={[...langs]}
            contentMaxHeight={isDialogExpanded ? 'calc(100vh - 310px)' : 'calc(0.8*100vh - 310px)'}
            i18nConfig={{
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                useTranslation,
            }}
        />
    );
};

/** Interface that describes the props for a IFormDesignerProps component. */
export interface IFormDesignerProps {
    submitRef: React.RefObject<HTMLFormElement>;

    /** The current doctype of the document. */
    docType: IProjectDocType;

    /** Details of the current opened document. */
    documentDetails: IDocumentDetailsEntry;

    /** Defines if formdesigner has default formData. */
    defaultFormData: object;
}

/** Interface for the object file that is returned by the formdesigner */
export interface IFormFile {
    color: string;
    icon: string;
    text: string;
    value: {
        replicaId: string;
        IdDocIndex?: string;
        fieldName?: string;
        fileName?: string;
        fieldIndex?: string;
        UniversalID?: string;
        docNumber?: string;
        docIndex?: string;
    };
}
